"use client";

import type { ComponentProps } from "react";

import {
  SectionNavigator,
  type LoopRange,
} from "@/components/ui/organisms/section-navigator";
import { TrackList } from "@/components/ui/organisms/track-list";

import { useCurrentMeasure } from "@/hooks/use-current-measure";

import type { AnalysisRun, AnalysisSection, AnalysisPhrase } from "@/lib/music/analyzer-types";
import type { SongControler } from "@/lib/track/controler/song-controler";

import { SongHeader } from "./song-header";
import { SongListeningBand } from "./song-listening-band";

/**
 * Subscribes to useCurrentMeasure locally so a measure change re-renders only
 * this wrapper (and its SectionNavigator child), not the whole StudioPage , 
 * which previously caused a 30 ms cascade across every studio child every 2 s.
 */
function StudioSectionNavigator(
  props: Omit<ComponentProps<typeof SectionNavigator>, "currentMeasure">,
) {
  const currentMeasure = useCurrentMeasure();

  return <SectionNavigator {...props} currentMeasure={currentMeasure} />;
}

interface DefaultStudioSidebarProps {
  songTitle: string | null;
  songComposer: string | null;
  onCloseSong: () => void;
  /**
   * Re-opens the `PieceOverview` overlay. Optional, passes through to
   * `SongHeader`, which only adds the "ⓘ Aperçu" affordance when the
   * callback is supplied.
   */
  onShowOverview?: () => void;
  /** Controler of the loaded piece, drives the listening band at the bottom. */
  songControler: SongControler | null;

  sections: AnalysisSection[];
  /** Le découpage au-dessus des sections : les prises d'un replay. Vide sinon. */
  runs: AnalysisRun[];
  phraseItems: AnalysisPhrase[];
  loop: LoopRange | null;
  onLoopChange: (loop: LoopRange | null, activate?: boolean) => void;
  onSeekToMeasure: (measure: number) => void;
  isLooping?: boolean;
  onToggleLooping?: () => void;
}

export function DefaultStudioSidebar({
  songTitle,
  songComposer,
  onCloseSong,
  onShowOverview,
  songControler,
  sections,
  runs,
  phraseItems,
  loop,
  onLoopChange,
  onSeekToMeasure,
  isLooping,
  onToggleLooping,
}: DefaultStudioSidebarProps) {
  return (
    <>
      <SongHeader
        title={songTitle}
        composer={songComposer}
        onClose={onCloseSong}
        onShowOverview={onShowOverview}
      />

      <div className="flex-1 min-h-0 overflow-hidden">
        <StudioSectionNavigator
          sections={sections}
          runs={runs}
          phrases={phraseItems}
          loop={loop}
          onLoopChange={onLoopChange}
          onSeekToMeasure={onSeekToMeasure}
          isLooping={isLooping}
          onToggleLooping={onToggleLooping}
        />
      </div>

      <SongListeningBand songControler={songControler} />

      {/* Ne se rend que pour un morceau qui ne se réduit pas aux deux mains
          (cf. `TrackList`). Dans le cas nominal il ne rend rien, et ses 174 px
          reviennent au `SectionNavigator` ci-dessus, qui est en `flex-1`. */}
      <TrackList />
    </>
  );
}
